import React, { useState, useEffect, useRef } from 'react';

const TerminalInput = ({ onSendMessage, inputDisabled, inputPlaceholder, isTyping, userTag = 'guest' }) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const initialViewportHeight = useRef(window.innerHeight);

  // Mobile keyboard detection
  useEffect(() => {
    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const heightDifference = initialViewportHeight.current - currentHeight;
      
      // Consider keyboard visible if viewport height decreased by more than 150px
      const keyboardVisible = heightDifference > 150;
      setIsKeyboardVisible(keyboardVisible);
      
      // Add/remove CSS class for keyboard visibility
      if (keyboardVisible) {
        document.body.classList.add('keyboard-visible');
      } else {
        document.body.classList.remove('keyboard-visible');
      }
    };

    // Listen for viewport changes (keyboard show/hide)
    window.addEventListener('resize', handleResize);
    
    // Also listen for focus events on input elements
    const handleFocus = () => {
      // Small delay to allow keyboard to appear
      setTimeout(() => {
        const currentHeight = window.innerHeight;
        const heightDifference = initialViewportHeight.current - currentHeight;
        if (heightDifference > 150) {
          setIsKeyboardVisible(true);
          document.body.classList.add('keyboard-visible');
        }
      }, 300);
    };

    const handleBlur = () => {
      // Small delay to allow keyboard to hide
      setTimeout(() => {
        const currentHeight = window.innerHeight;
        const heightDifference = initialViewportHeight.current - currentHeight;
        if (heightDifference <= 150) {
          setIsKeyboardVisible(false);
          document.body.classList.remove('keyboard-visible');
        }
      }, 300);
    };

    // Add focus/blur listeners to all input elements
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
      document.body.classList.remove('keyboard-visible');
    };
  }, []);

  // Focus the input when it becomes available
  useEffect(() => {
    if (!inputDisabled && inputRef.current) {
      // Use setTimeout to ensure the input is fully rendered before focusing
      const focusTimeout = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50); // Reduced timeout for better responsiveness
      
      return () => clearTimeout(focusTimeout);
    }
  }, [inputDisabled]); // This will run on mount and when inputDisabled changes

  // Also focus when the component becomes visible (after typing/typewriter completes)
  useEffect(() => {
    if (!isTyping && !inputDisabled && inputRef.current) {
      const focusTimeout = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100); // Slightly longer timeout for typewriter completion
      
      return () => clearTimeout(focusTimeout);
    }
  }, [isTyping, inputDisabled]);

  useEffect(() => {
    // Focus the input when clicking anywhere in the window (if not disabled)
    const handleWindowClick = (e) => {
      if (!inputDisabled && inputRef.current && 
          !e.target.closest('button') && 
          !e.target.closest('a') && 
          !e.target.closest('.action-item') &&
          !e.target.closest('.orb-complex-container')) {
        e.preventDefault();
        inputRef.current.focus();
      }
    };

    document.addEventListener('click', handleWindowClick);

    return () => {
      document.removeEventListener('click', handleWindowClick);
    };
  }, [inputDisabled]);

  const handleSubmit = () => {
    if (inputValue.trim() && !inputDisabled) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  const handleFocus = () => {
    setIsFocused(true);
    
    // Scroll to bottom when input is focused on mobile
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        const chatMessages = document.getElementById('chatMessages');
        if (chatMessages) {
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
      }, 100);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Scroll to bottom when keyboard becomes visible
  useEffect(() => {
    if (isKeyboardVisible) {
      const scrollToBottom = () => {
        const chatMessages = document.getElementById('chatMessages');
        if (chatMessages) {
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
      };
      
      // Scroll immediately and after a short delay
      scrollToBottom();
      setTimeout(scrollToBottom, 100);
      
      // Also scroll when input value changes (user is typing)
      const handleInputChange = () => {
        setTimeout(scrollToBottom, 50);
      };
      
      if (inputRef.current) {
        inputRef.current.addEventListener('input', handleInputChange);
        return () => {
          if (inputRef.current) {
            inputRef.current.removeEventListener('input', handleInputChange);
          }
        };
      }
    }
  }, [isKeyboardVisible]);

  // Don't render the terminal input when AI is typing or typewriter is active
  if (isTyping) {
    return null;
  }

  return (
    <div ref={containerRef} className="terminal-input-line">
      <span className="terminal-prompt" aria-hidden="true">{userTag} ~ % </span>
      <textarea
        ref={inputRef}
        className="terminal-inline-input"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={inputDisabled}
        placeholder={inputDisabled ? inputPlaceholder : ""}
        autoComplete="off"
        spellCheck="false"
        aria-label="Terminal input"
        rows={1}
      />
    </div>
  );
};

export default TerminalInput;
